import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject,
    UploadMetadata
} from 'firebase/storage';
import {
    collection,
    doc,
    addDoc,
    getDocs,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    Timestamp
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { storage, db } from '../../../firebase/firebase.config';
import {
    UserDocument,
    DocumentUploadRequest,
    DocumentUploadResponse,
    DocumentListResponse,
    DeleteDocumentResponse,
    ALLOWED_FILE_TYPES,
    MAX_FILE_SIZE
} from '../types/document.types';

export class DocumentService {
    /**
     * Upload a document (file or link) for the current user
     */
    static async uploadDocument(
        request: DocumentUploadRequest,
        options?: {
            additionalDocumentsLimit?: number;
        }
    ): Promise<DocumentUploadResponse> {
        const additionalDocumentsLimit = options?.additionalDocumentsLimit || 3;
        
        try {
            const auth = getAuth();
            const user = auth.currentUser;

            if (!user) {
                return {
                    status: 'error',
                    message: 'User not authenticated'
                };
            }

            // For LinkedIn profile (link only)
            if (request.fileType === 'linkedin') {
                if (!request.linkUrl) {
                    return {
                        status: 'error',
                        message: 'LinkedIn profile URL is required'
                    };
                }

                // Validate LinkedIn URL format
                if (!this.isValidLinkedInUrl(request.linkUrl)) {
                    return {
                        status: 'error',
                        message: 'Please provide a valid LinkedIn profile URL'
                    };
                }

                // Check if user already has a LinkedIn profile
                const existingDocs = await this.getUserDocuments(user.uid);
                if (existingDocs.status === 'success') {
                    const existingLinkedIn = existingDocs.documents.filter(doc => doc.fileType === 'linkedin');
                    if (existingLinkedIn.length >= 1) {
                        return {
                            status: 'error',
                            message: 'You can only have one LinkedIn profile. Please delete the existing one first.'
                        };
                    }
                }

                // Save to Firestore
                const documentData = {
                    userId: user.uid,
                    fileName: 'LinkedIn Profile',
                    fileType: request.fileType,
                    linkUrl: request.linkUrl,
                    uploadedAt: serverTimestamp(),
                };

                const docRef = await addDoc(collection(db, 'userDocuments'), documentData);

                const userDocument: UserDocument = {
                    id: docRef.id,
                    fileName: 'LinkedIn Profile',
                    fileType: request.fileType,
                    linkUrl: request.linkUrl,
                    uploadedAt: new Date(),
                };

                return {
                    status: 'success',
                    message: 'LinkedIn profile saved successfully!',
                    document: userDocument
                };
            }

            // For file uploads (CV and additional documents)
            if (!request.file) {
                return {
                    status: 'error',
                    message: 'File is required'
                };
            }

            // Validate file size
            if (request.file.size > MAX_FILE_SIZE) {
                return {
                    status: 'error',
                    message: 'File size must be less than 10MB'
                };
            }

            // Validate file type
            const allowedTypes = ALLOWED_FILE_TYPES[request.fileType];
            if (!allowedTypes.includes(request.file.type)) {
                return {
                    status: 'error',
                    message: `File type not supported. Allowed types: ${allowedTypes.join(', ')}`
                };
            }

            // Check if user already has this type of document and enforce limits
            const existingDocs = await this.getUserDocuments(user.uid);
            if (existingDocs.status === 'success') {
                const existingOfType = existingDocs.documents.filter(doc => doc.fileType === request.fileType);

                if (request.fileType === 'cv' && existingOfType.length >= 1) {
                    return {
                        status: 'error',
                        message: 'You can only upload one CV. Please delete the existing one first.'
                    };
                }

                if (request.fileType === 'additional' && existingOfType.length >= additionalDocumentsLimit) {
                    return {
                        status: 'error',
                        message: `You can only upload up to ${additionalDocumentsLimit} additional documents. Please delete some existing ones first.`
                    };
                }
            }

            // Create file path with proper folder structure
            const timestamp = Date.now();
            const fileExtension = request.file.name.split('.').pop();
            const fileName = `${request.fileType}_${timestamp}.${fileExtension}`;
            const filePath = `users/${user.uid}/global-talent-visa/${request.fileType}/${fileName}`;

            // Upload to Firebase Storage
            const storageRef = ref(storage, filePath);
            const metadata: UploadMetadata = {
                contentType: request.file.type,
                customMetadata: {
                    originalName: request.file.name,
                    userId: user.uid,
                    fileType: request.fileType
                }
            };

            const uploadResult = await uploadBytes(storageRef, request.file, metadata);
            const downloadURL = await getDownloadURL(uploadResult.ref);

            // Save metadata to Firestore
            const documentData = {
                userId: user.uid,
                fileName: request.file.name,
                fileType: request.fileType,
                fileUrl: downloadURL,
                storagePath: filePath,
                fileSize: request.file.size,
                mimeType: request.file.type,
                uploadedAt: serverTimestamp(),
            };

            const docRef = await addDoc(collection(db, 'userDocuments'), documentData);

            const userDocument: UserDocument = {
                id: docRef.id,
                fileName: request.file.name,
                fileType: request.fileType,
                fileUrl: downloadURL,
                uploadedAt: new Date(),
                fileSize: request.file.size,
                mimeType: request.file.type,
            };

            return {
                status: 'success',
                message: 'Document uploaded successfully!',
                document: userDocument
            };

        } catch (error) {
            console.error('Error uploading document:', error);
            return {
                status: 'error',
                message: error instanceof Error ? error.message : 'Failed to upload document'
            };
        }
    }

    /**
     * Get all documents for the current user
     */
    static async getUserDocuments(userId?: string): Promise<DocumentListResponse> {
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            const targetUserId = userId || user?.uid;

            if (!targetUserId) {
                return {
                    status: 'error',
                    message: 'User not authenticated',
                    documents: []
                };
            }

            const q = query(
                collection(db, 'userDocuments'),
                where('userId', '==', targetUserId),
                orderBy('uploadedAt', 'desc')
            );

            const querySnapshot = await getDocs(q);
            const documents: UserDocument[] = [];

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                documents.push({
                    id: doc.id,
                    fileName: data.fileName,
                    fileType: data.fileType,
                    fileUrl: data.fileUrl,
                    linkUrl: data.linkUrl,
                    uploadedAt: data.uploadedAt instanceof Timestamp ? data.uploadedAt.toDate() : new Date(data.uploadedAt),
                    fileSize: data.fileSize,
                    mimeType: data.mimeType,
                });
            });

            return {
                status: 'success',
                message: 'Documents retrieved successfully',
                documents
            };

        } catch (error) {
            console.error('Error getting user documents:', error);
            return {
                status: 'error',
                message: error instanceof Error ? error.message : 'Failed to retrieve documents',
                documents: []
            };
        }
    }

    /**
     * Delete a document
     */
    static async deleteDocument(documentId: string, storagePath?: string): Promise<DeleteDocumentResponse> {
        try {
            const auth = getAuth();
            const user = auth.currentUser;

            if (!user) {
                return {
                    status: 'error',
                    message: 'User not authenticated'
                };
            }

            // Delete from Firebase Storage if it's a file upload
            if (storagePath) {
                try {
                    const storageRef = ref(storage, storagePath);
                    await deleteObject(storageRef);
                } catch (storageError) {
                    console.warn('Error deleting from storage (file may not exist):', storageError);
                    // Continue with Firestore deletion even if storage deletion fails
                }
            }

            // Delete from Firestore
            await deleteDoc(doc(db, 'userDocuments', documentId));

            return {
                status: 'success',
                message: 'Document deleted successfully'
            };

        } catch (error) {
            console.error('Error deleting document:', error);
            return {
                status: 'error',
                message: error instanceof Error ? error.message : 'Failed to delete document'
            };
        }
    }

    /**
     * Validate LinkedIn URL format
     */
    private static isValidLinkedInUrl(url: string): boolean {
        try {
            // More permissive regex that allows special characters and encoded URLs
            const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9\-_%]+(\/.*)?$/;
            
            // Also check if it's a valid URL and contains linkedin.com/in/
            const urlObj = new URL(url);
            return linkedinRegex.test(url) && 
                   urlObj.hostname.includes('linkedin.com') && 
                   urlObj.pathname.startsWith('/in/');
        } catch (error) {
            // If URL parsing fails, it's not a valid URL
            return false;
        }
    }

    /**
     * Get file icon based on file type
     */
    static getFileIcon(mimeType?: string): string {
        if (!mimeType) return '📄';

        if (mimeType.includes('pdf')) return '📕';
        if (mimeType.includes('word') || mimeType.includes('document')) return '📘';
        if (mimeType.includes('image')) return '🖼️';
        return '📄';
    }

    /**
     * Format file size for display
     */
    static formatFileSize(bytes?: number): string {
        if (!bytes) return '';

        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    }
} 